import { ApplicationContext } from '@upside/spa-shared-context';

import {
  basePaths,
  navigationAppName,
  paths,
  amiEventsUiAppName,
} from 'shell/const';
import { registerApplication } from 'single-spa';
import {
  constructRoutes,
  constructApplications,
  constructLayoutEngine,
} from 'single-spa-layout';

import { registerAppHeader } from './frontends/app-header';
import { registerDevTools } from './frontends/dev-tools';
import { registerGlobalHeader } from './frontends/global-header';
import { registerInfraflexAiAssistant } from './frontends/infraflex-ai-assistant';
import { registerLogout } from './frontends/logout';

/* eslint-disable @typescript-eslint/no-var-requires */
const contentAppError = require('./content-app-error.html');
const contentAppLoader = require('./content-app-loader.html');
const navError = require('./nav-error.html');

/**
 * List of paths where the navigation should not be shown
 */
const PATHS_WITHOUT_NAVIGATION = [
  `/${paths.DOMESTIC_DASHBOARD}`,
  `/${paths.VERIFY_MANAGED_SUBSCRIPTION}`,
  `/${paths.ACCOUNT}`,
];

/**
 * Registers the micro-frontend applications by building the routes from the template file as well
 * as registering any additional applications that need to be registered programmatically.
 *
 * @param context - The application context that will be passed to the applications
 */
export const registerApplications = (context: ApplicationContext): void => {
  const layoutRoot = document.querySelector('#single-spa-layout');

  if (!layoutRoot) {
    throw new Error('Could not find template element');
  }

  const routes = constructRoutes(layoutRoot, {
    errors: {
      contentAppError,
      navError,
    },
    loaders: {
      contentAppLoader,
    },
    props: {},
  });

  const applications = constructApplications({
    routes,
    loadApp: ({ name }: any) => System.import(name),
  });

  constructLayoutEngine({
    routes,
    applications,
  });

  // single-spa layout doesn't allow for complex activeWhen functions or app-specific usage of customProps, so we adapt the application configs to our needs
  const adaptedApplicationConfigs = applications.map((application) => {
    const adaptedApplicationConfig = {
      ...application,
      customProps: {
        ...application.customProps,
        basePath: basePaths[application.name],
        context,
      },
    };

    if (application.name === navigationAppName) {
      // Add the navigation when the user is logged in and not on the domestic dashboard or the verify managed subscription page
      return {
        ...adaptedApplicationConfig,
        activeWhen: (location: Location) =>
          context.authentication.loggedIn &&
          !PATHS_WITHOUT_NAVIGATION.some((path) =>
            location.pathname.startsWith(path),
          ),
      };
    }

    if (application.name === amiEventsUiAppName) {
      return {
        ...adaptedApplicationConfig,
        customProps: {
          ...adaptedApplicationConfig.customProps,
          viewType: 'verification',
        },
      };
    }

    return adaptedApplicationConfig;
  });
  adaptedApplicationConfigs.forEach(registerApplication);

  // Logout isn't a single-spa app, so can't be declared in the template
  registerLogout(context);

  // Register the AI assistant/Dev tools when their modal is opened for the first time
  registerInfraflexAiAssistant(context);
  registerDevTools(context);

  // AppHeader and Global Header are registered programmatically as only want to load them based on a feature flag
  registerGlobalHeader(context);
  registerAppHeader(context);
};
